import { RefObject, useEffect } from 'react'

export interface UseScrollLockOptions {
  enabled: boolean
  drawerRef: RefObject<HTMLElement | null>
}

/**
 * Prevents background page scrolling while a drawer is open on mobile.
 *
 * `overflow: hidden` on html/body is insufficient on mobile browsers —
 * touch-driven scrolling can bypass it. This hook registers a document-level
 * touchmove listener that:
 * - Prevents all touches outside the drawer (blocks background scroll).
 * - Prevents touches inside the drawer on non-scrollable areas (drag handle,
 *   header, input container) so they don't propagate to the body.
 * - Allows touches inside scrollable content areas (e.g., chat messages
 *   with overflow-y: scroll/auto).
 *
 * Opt-in via the `lockBodyScroll` prop on AppDrawer. Use for full-width
 * mobile bottom sheets. Do NOT use for partial-page widgets where the user
 * should still be able to scroll the page behind.
 */
export const useScrollLock = ({
  enabled,
  drawerRef,
}: UseScrollLockOptions): void => {
  useEffect(() => {
    if (!enabled) return undefined

    const handleTouchMove = (e: TouchEvent) => {
      const drawer = drawerRef.current
      if (!drawer) return

      // Touches outside the drawer — always prevent
      if (!drawer.contains(e.target as Node)) {
        e.preventDefault()
        return
      }

      // Touches inside the drawer — prevent unless in a scrollable area
      let el = e.target as HTMLElement | null
      while (el && el !== drawer) {
        if (el.scrollHeight > el.clientHeight) {
          const { overflowY } = getComputedStyle(el)
          if (overflowY === 'scroll' || overflowY === 'auto') {
            return // Allow — this is a scrollable content area
          }
        }
        el = el.parentElement
      }
      e.preventDefault()
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    return () => {
      document.removeEventListener('touchmove', handleTouchMove)
    }
  }, [enabled, drawerRef])
}
